/**
 * Cimagepreview Component
 * Reusable, presentational image preview for a plain URL: a square thumbnail
 * (the established attachment look) plus a click-to-open full-size lightbox.
 *
 * Shared by markdown images (Cmarkdownimage) and attachment thumbnails
 * (ChatImageAttachment). The model/3rd-party URL is treated as untrusted.
 *
 * Modal ownership:
 *   - `onClick` provided  → the host owns the modal (e.g. attachments open the
 *     richer FilePreviewModal/Board); this component just calls `onClick`.
 *   - `onClick` omitted    → the built-in Cstretchmodal lightbox opens on click.
 */

"use client";

import type { CSSProperties, ReactElement } from "react";
import { DEFAULT_IMAGE_PREVIEW_SIZE } from "./image-preview-constants";
import type { ImagePreviewFrame } from "./image-preview-frame";
import { CimagepreviewLightbox } from "./CimagepreviewLightbox";
import { CimagepreviewThumbnail } from "./CimagepreviewThumbnail";
import { useImagePreviewController } from "./image-preview-state";

export interface CimagepreviewProps {
  src: string;
  alt: string;
  title?: string;
  /** Square thumbnail edge in px. Default 240 = single-attachment size. */
  size?: number;
  /** Host is still resolving the URL → show a skeleton (attachments). */
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
  /** When provided, the host owns the modal; omit → built-in lightbox. */
  onClick?: () => void;
  /** Optional framing recipe; omitted → the shipped square ModalCard preview. */
  frame?: ImagePreviewFrame;
}

export function Cimagepreview({
  src,
  alt,
  title,
  size = DEFAULT_IMAGE_PREVIEW_SIZE,
  loading = false,
  className,
  style,
  onClick,
  frame,
}: CimagepreviewProps): ReactElement {
  const preview = useImagePreviewController({ src, loading, onClick });
  return (
    <>
      <CimagepreviewThumbnail
        src={src}
        alt={alt}
        title={title}
        size={size}
        className={className}
        style={style}
        status={preview.status}
        clickable={preview.clickable}
        onClick={preview.handleRootClick}
        onError={preview.handleImageError}
        frame={frame}
      />
      <CimagepreviewLightbox
        enabled={preview.usesBuiltInLightbox}
        visible={preview.isModalOpen}
        onClose={preview.closeModal}
        src={src}
        alt={alt}
        title={title}
      />
    </>
  );
}
